import { useState } from "react";
import { ApiError, type Workspace, type WorkspaceModules } from "@nahal/api-client";
import { Button, EmptyState, ErrorState, LoadingBlock, ModuleChecklist, Sheet, StatusBadge, TextAreaField } from "@nahal/ui";
import { useSetWorkspaceModules, useWorkspaceModules } from "../../lib/queries";

export function WorkspaceModulesSheet({ workspace, onClose }: { workspace: Workspace; onClose: () => void }) {
  const query = useWorkspaceModules(workspace.externalRef ? workspace.id : "");
  return (
    <Sheet
      open
      title={"Modules for " + workspace.name}
      description="Choose which available modules your team uses in this workspace."
      onClose={onClose}
    >
      {!workspace.externalRef ? (
        <EmptyState title="Workspace is being set up" description="Modules will be available once this workspace is linked to the product." />
      ) : query.isFetching ? (
        <LoadingBlock label="Loading current module access" />
      ) : query.isError ? (
        <ErrorState
          message={query.error instanceof ApiError ? query.error.message : "Could not load module access."}
          action={<Button variant="outline" onClick={() => query.refetch()}>Try again</Button>}
        />
      ) : query.data ? (
        <WorkspaceModulesForm
          key={String(query.data.grantRevision) + ":" + query.data.settingsRevision + ":" + query.data.syncStatus}
          workspaceId={workspace.id}
          state={query.data}
          onClose={onClose}
        />
      ) : <EmptyState title="No module access reported" />}
    </Sheet>
  );
}

function WorkspaceModulesForm({
  workspaceId, state, onClose,
}: {
  workspaceId: string;
  state: WorkspaceModules;
  onClose: () => void;
}) {
  const [values, setValues] = useState<Record<string, boolean>>(() => Object.fromEntries(state.modules.map((module) => [module.id, module.granted && module.enabled])));
  const [reason, setReason] = useState("");
  const save = useSetWorkspaceModules(workspaceId);
  const changed = state.modules.some((module) => (module.granted && values[module.id]) !== (module.granted && module.enabled));
  const awaitingApplication = state.syncStatus === "PENDING" || state.syncStatus === "FAILED";
  const readOnly = !state.canManage || awaitingApplication;

  if (state.modules.length === 0) {
    return <EmptyState title="No configurable modules" description="There are no module settings for this product yet." />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">Available modules</h3>
        {awaitingApplication && <StatusBadge status="Update pending" tone="warning" />}
      </div>
      {awaitingApplication && (
        <p role="status" className="text-sm text-[var(--text-secondary)]">
          Nahal Labs is applying a workspace access update. Module settings can be changed once it has finished.
        </p>
      )}
      {state.syncError && <ErrorState message="The workspace access update needs attention. Contact Nahal Labs." />}
      {!state.canManage && (
        <p className="text-sm text-[var(--text-secondary)]">Only an organisation owner or administrator can change module settings.</p>
      )}
      <ModuleChecklist
        mode="settings"
        items={state.modules}
        values={values}
        disabled={readOnly || save.isPending}
        applied={!awaitingApplication}
        onChange={(id, checked) => setValues((current) => ({ ...current, [id]: checked }))}
      />
      {!readOnly && (
        <TextAreaField
          label="Reason for the change"
          hint="Your organisation can refer to this in its activity history."
          required
          maxLength={500}
          disabled={save.isPending}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      )}
      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
        <Button variant="outline" onClick={onClose} disabled={save.isPending}>Close</Button>
        {!readOnly && (
          <Button
            variant="solid"
            disabled={save.isPending || !changed || !reason.trim()}
            onClick={() => save.mutate({
              expectedRevision: state.settingsRevision,
              enabled: Object.fromEntries(state.modules.map((module) => [module.id, module.granted && Boolean(values[module.id])])),
              reason: reason.trim(),
            })}
          >
            {save.isPending ? "Saving..." : "Save settings"}
          </Button>
        )}
      </div>
    </div>
  );
}
